import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, ShieldCheck, Truck, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import PageHead from '@/components/PageHead';
import { useQuote } from '@/context/QuoteContext';
import { fetchProduct, CATEGORIES } from '@/lib/api';

export default function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { openQuote } = useQuote();

  useEffect(() => {
    setLoading(true);
    fetchProduct(slug)
      .then(setProduct)
      .finally(() => setLoading(false));
  }, [slug]);

  const cat = product && CATEGORIES.find((c) => c.slug === product.category);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-2 gap-8">
        <Skeleton className="aspect-square rounded-2xl" />
        <div className="space-y-3">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h1 className="font-heading text-2xl font-semibold">Produit introuvable</h1>
        <Button asChild className="mt-4 rounded-xl"><Link to="/boutique"><ArrowLeft className="w-4 h-4 mr-2" />Retour boutique</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="product-page">
      <PageHead title={product.name} description={product.short_description || `Demandez un devis pour ${product.name} chez ALOFI SA.`} />
      <nav className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap" aria-label="breadcrumb">
        <Link to="/" className="hover:text-foreground">Accueil</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/boutique" className="hover:text-foreground">Boutique</Link>
        <ChevronRight className="w-3 h-3" />
        {cat && <Link to={`/boutique?category=${cat.slug}`} className="hover:text-foreground">{cat.name}</Link>}
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground line-clamp-1">{product.name}</span>
      </nav>

      <div className="mt-6 grid md:grid-cols-2 gap-8 items-start">
        <div className="rounded-3xl overflow-hidden bg-white border border-border p-6 flex items-center justify-center">
          <img src={product.image} alt={product.name} className="w-full max-h-[480px] object-contain" />
        </div>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {cat && <Badge variant="secondary" className="tracking-[0.06em] uppercase text-[10px]">{cat.name}</Badge>}
            {product.brand && <Badge variant="outline" className="tracking-[0.06em] uppercase text-[10px]">{product.brand}</Badge>}
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="product-name">{product.name}</h1>
          {product.short_description && (
            <p className="mt-3 text-muted-foreground leading-relaxed">{product.short_description}</p>
          )}

          {product.description && (
            <>
              <Separator className="my-6" />
              <p className="text-sm leading-relaxed text-foreground/85">{product.description}</p>
            </>
          )}

          {product.specs?.length > 0 && (
            <>
              <Separator className="my-6" />
              <h3 className="font-heading font-semibold text-sm uppercase tracking-[0.08em] text-muted-foreground">Caractéristiques</h3>
              <ul className="mt-3 space-y-2 text-sm">
                {product.specs.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <BadgeCheck className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <Card className="mt-8 p-5 rounded-2xl border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[hsl(186_55%_38%)]" />
              </div>
              <div>
                <div className="text-sm font-medium">Aucun prix affiché</div>
                <div className="text-xs text-muted-foreground">Tarif dégrressif selon volume — demandez votre devis B2B.</div>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button onClick={() => openQuote(product)} size="lg" className="rounded-xl bg-[hsl(186_55%_38%)] hover:bg-[hsl(186_55%_32%)]" data-testid="product-quote-button">
                Demander un devis
              </Button>
              <Button asChild variant="secondary" size="lg" className="rounded-xl">
                <Link to="/boutique"><ArrowLeft className="w-4 h-4 mr-2" />Retour</Link>
              </Button>
            </div>
            <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-[hsl(186_55%_38%)]" />Livraison France</span>
              <span className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-[hsl(186_55%_38%)]" />Réponse 24-48h</span>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
